import { FormProvider, useForm } from 'react-hook-form';
import Modal from '@/components/modal/Modal.tsx';
import ModalHeader from '@/components/modal/ModalHeader.tsx';
import ModalContent from '@/components/modal/ModalContent.tsx';
import ModalFooter from '@/components/modal/ModalFooter.tsx';
import ActionButton from '@/components/buttons/ActionButton.tsx';
import { debugZodResolver } from '@/lib/debugZodResolver.ts';
import CompanyLogoForm from '@/features/dashboard/company/components/CompanyLogoForm.tsx';
import useUpdateCompanyInfo from '@/features/dashboard/company/hooks/useUpdateCompanyInfo.ts';
import {
  companyLogoSchema,
  CompanyLogoFormData,
  CompanyLogoInputFormData,
} from '@/features/dashboard/company/schemas/companyLogoSchema.ts';
import CompanyInfoDto from '@/features/dashboard/company/types/CompanyInfoDto.ts';
import { FileAction } from '@/schemas/fileFieldSchema.ts';

interface EditCompanyLogoModalProps {
  companyInfo: CompanyInfoDto;
  onClose: () => void;
}

const EditCompanyLogoModal = ({
  companyInfo,
  onClose,
}: EditCompanyLogoModalProps) => {
  const updateMutation = useUpdateCompanyInfo();

  const methods = useForm<CompanyLogoInputFormData>({
    resolver: debugZodResolver(companyLogoSchema),
    values: {
      logo: {
        action: FileAction.NO_ACTION,
        fileId: companyInfo.companyLogoFileId ?? undefined,
        fileUrl: companyInfo.companyLogoDownloadUrl ?? undefined,
      },
    },
  });

  const { handleSubmit } = methods;

  // Zapis idzie jednym PUT-em na całą firmę, więc nazwa jedzie z nim nietknięta.
  const onSubmit = (data: CompanyLogoFormData) => {
    updateMutation.mutate(
      { data: { companyName: companyInfo.companyName, logo: data.logo } },
      { onSuccess: () => onClose() },
    );
  };

  return (
    <Modal onClose={onClose} className="max-w-[520px]">
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <ModalHeader title="Edytuj logo firmy" onClose={onClose} />
          <ModalContent>
            <CompanyLogoForm />
          </ModalContent>
          <ModalFooter>
            <ActionButton
              tone="ghost"
              size="sm"
              type="button"
              onClick={onClose}
            >
              Anuluj
            </ActionButton>
            <ActionButton
              tone="accent"
              size="sm"
              type="submit"
              isLoading={updateMutation.isPending}
            >
              Zapisz
            </ActionButton>
          </ModalFooter>
        </form>
      </FormProvider>
    </Modal>
  );
};

export default EditCompanyLogoModal;
